import {
  normalizeCalibreKey,
  resolveCalibreForRef,
  type ResolvedRefCalibre,
} from '../../services/calibreResolution';
import type { CalibreTier, CustomCalibreEntry } from '../../services/firebase';
import type { CalibreDualWriteFlags } from './calibreBulkUpdate';

/**
 * The alias breadth every AI-calibre path resolves at.
 *
 * `crossBrandUnscopedAliases` is on, matching the References bulk action bar
 * and deliberately unlike the bulk importer. A brand routinely fits a base
 * movement it did not make, and the reference's calibre string then carries a
 * universal nickname for it — an accepted "Cal. 2824" on a Tissot reference has
 * to reach the ETA-owned "ETA 2824-2". An alias scoped to a specific brand is
 * unaffected and still refuses every other brand.
 *
 * Shared by the apply itself and by the review dialog's pre-flight check, so the
 * warning an admin reads is produced by the same rules as the write it is
 * warning about. Two breadths would let the dialog promise a link the apply then
 * refuses (or, worse, stay silent about one it is about to make).
 */
const AI_CALIBRE_MATCH_OPTIONS = { crossBrandUnscopedAliases: true } as const;

/**
 * What the reference editor should write when an admin accepts an AI calibre
 * suggestion.
 *
 * The bulk action bar's {@link import('./calibreBulkUpdate').buildCalibreBulkUpdate}
 * builds a Firestore payload directly, because that path writes documents. This
 * one cannot: the editor's calibre apply drives ~45 pieces of React state that
 * `handleSave` later serialises, so the decisions have to come back as
 * instructions the component carries out rather than as field paths.
 *
 * Same three rules underneath, and the same resolver, so both paths link the
 * same reference to the same calibre.
 */
export interface AiCalibreApplyPlan {
  /** The resolved calibre, or `null` when the accepted name named none. */
  resolved: ResolvedRefCalibre<CustomCalibreEntry> | null;
  /**
   * `custom_calibres/{id}` to link, or `null` when nothing resolved.
   *
   * `null` means "no new link" and never "unlink": a reference that already
   * carries a `calibreId` keeps it when an accepted name fails to resolve. The
   * admin accepted a calibre, not a request to sever the existing one.
   */
  calibreId: string | null;
  /** The legacy free-text `calibre` value to set, or `null` to leave it alone. */
  calibreString: string | null;
  /** Whether to copy the calibre spec into the legacy `movement.*` form state. */
  applyLegacyMovement: boolean;
  /**
   * Which string carried the link.
   *
   * `'accepted'` — the name the admin ticked in the review table, which is what
   * the dialog's pre-flight check already reported on. `'normalized'` — the
   * `lookupCalibre` callable's name for it, reached only because the accepted
   * name resolved to nothing (see {@link buildAiCalibreApply}). `null` — nothing
   * resolved.
   *
   * The caller announces the `'normalized'` case: the dialog has just told the
   * admin this name matches no calibre, and a link appearing anyway needs to say
   * where it came from rather than quietly contradict the warning.
   */
  resolvedVia: 'accepted' | 'normalized' | null;
}

/**
 * Decide the calibre write for an accepted AI suggestion.
 *
 * One resolver decides both halves, exactly as the bulk bar does: the
 * `calibreId` link and any legacy movement specs the caller writes beside it
 * both come off the entry {@link resolveCalibreForRef} returned, so a reference
 * can never point at one calibre while carrying another's specs.
 *
 * The two flags subtract from the plan; neither adds anything. `calibreId` is
 * always offered when the name resolves, because that link is the migration's
 * destination rather than one of the legacy writes being retired.
 *
 * `normalizedName` is what the `lookupCalibre` callable made of the accepted
 * string, and stays the legacy string's value when it is present. The resolver
 * decides the LINK; it deliberately does not restyle the free-text column here
 * the way a bulk update does, so this keeps writing exactly the string the
 * editor wrote before the `calibreId` half existed.
 *
 * It is also the SECOND string offered to the resolver, when the accepted name
 * resolved to nothing. The two disagree routinely and in a way that used to cost
 * the link outright: the enrichReference prompt asks for a bare identifier
 * ("ETA 2824-2") while the catalogue mints canonical names with the word in
 * ("ETA Calibre 2824-2"), and `normalizeCalibreKey` folds the spelling but never
 * removes the word. `lookupCalibre` knows the fuller form, so trying it is the
 * difference between a link and a silently unlinked reference. It is a fallback
 * rather than a preference for the same reason `buildSlug` makes it untrustworthy
 * as a first choice: that slug strips punctuation ("P.9010" → "p9010"), so a
 * collision can hand back another calibre's page title entirely. Reaching it
 * only after an exact miss, and still demanding an exact catalogue match, keeps
 * that failure mode from inventing a link — and `resolvedVia` makes the rescue
 * visible when it does fire.
 *
 * The alias breadth is {@link AI_CALIBRE_MATCH_OPTIONS} — the same one the
 * review dialog's pre-flight check uses.
 */
export function buildAiCalibreApply(
  candidates: readonly CustomCalibreEntry[],
  appliedName: string,
  normalizedName: string | undefined,
  brand: { id?: string; name?: string },
  flags: CalibreDualWriteFlags,
): AiCalibreApplyPlan {
  const accepted = appliedName.trim();
  const normalized = (normalizedName ?? '').trim();

  let resolved = accepted
    ? resolveCalibreForRef(candidates, accepted, brand, AI_CALIBRE_MATCH_OPTIONS)
    : null;
  let resolvedVia: AiCalibreApplyPlan['resolvedVia'] = resolved ? 'accepted' : null;

  // Only when the accepted name found nothing, and only when the callable's name
  // is genuinely a different string — an identical key would re-run the same
  // failed lookup.
  if (
    !resolved &&
    normalized &&
    normalizeCalibreKey(normalized) !== normalizeCalibreKey(accepted)
  ) {
    resolved = resolveCalibreForRef(candidates, normalized, brand, AI_CALIBRE_MATCH_OPTIONS);
    if (resolved) resolvedVia = 'normalized';
  }

  return {
    resolved,
    calibreId: resolved ? resolved.calibreId : null,
    calibreString: flags.legacyCalibreString ? normalized || accepted : null,
    applyLegacyMovement: flags.legacyMovement,
    resolvedVia,
  };
}

/**
 * What checking an AI-proposed calibre name against the catalogue found.
 *
 * Produced BEFORE the review dialog opens, so the admin sees the consequence of
 * ticking the row rather than discovering it after Apply. Deliberately a plain
 * record rather than the resolver's own result: the dialog needs the two names
 * to compare and nothing else, and keeping it flat means the notice below is a
 * pure function testable without a calibre corpus.
 */
export interface AiCalibreMatchCheck {
  /** The AI's proposed name, trimmed. */
  proposed: string;
  /** The canonical name of the calibre it resolved to, or `null` if none did. */
  matchedName: string | null;
  /**
   * True when the catalogue could not be consulted at all — the name search
   * failed AND the full-scan fallback behind it failed too.
   *
   * Distinct from "resolved to nothing", and it must stay distinct: telling an
   * admin their calibre matches nothing because a read timed out is how a
   * duplicate calibre gets created.
   */
  unavailable: boolean;
}

/** An advisory note to render under an AI calibre suggestion. */
export interface AiCalibreMatchNotice {
  /**
   * `'unresolved'` and `'unavailable'` are cautions (amber); `'renamed'` is a
   * neutral statement of fact — the link is good, the admin just needs to see
   * that the name is about to change under them.
   */
  kind: 'unresolved' | 'renamed' | 'unavailable';
  /** Ready-to-render sentence. */
  message: string;
  /**
   * The calibre name to render emphasised inside `message`, or `null` when the
   * message names none. Lets the row bold the identity without parsing prose.
   */
  emphasis: string | null;
}

/**
 * Turn a catalogue check into the note the review row shows, or `null` when
 * there is nothing worth saying.
 *
 * Nothing is said in the ordinary case — the proposed name resolved to the
 * calibre of the same name — because a note on every calibre row is a note
 * nobody reads, and the amber ones stop standing out. The three cases that DO
 * speak are the three where applying the row does something other than what it
 * appears to:
 *
 *  - `unresolved` — the row writes free text and leaves the reference unlinked.
 *  - `renamed` — the row links, but to a calibre under a different name than the
 *    one displayed. This is the silent rename: an AI-proposed "Cal. 2824" landing
 *    on "ETA Calibre 2824-2" via an alias was previously indistinguishable from
 *    an exact hit.
 *  - `unavailable` — we do not know which of the two it is.
 *
 * A blank proposal says nothing: there is no suggestion to caution about.
 */
export function buildAiCalibreMatchNotice(
  check: AiCalibreMatchCheck,
): AiCalibreMatchNotice | null {
  const proposed = check.proposed.trim();
  if (!proposed) return null;

  if (check.unavailable) {
    return {
      kind: 'unavailable',
      message: 'Couldn’t check the catalogue — the link may not resolve.',
      emphasis: null,
    };
  }

  if (!check.matchedName) {
    return {
      kind: 'unresolved',
      message:
        `“${proposed}” matches no calibre in the catalogue. ` +
        'Applying will set the calibre text but leave this reference unlinked.',
      emphasis: proposed,
    };
  }

  // Compare through the shared key, so a pure casing or spelling-fold difference
  // ("eta caliber 2824-2" vs "ETA Calibre 2824-2") is not reported as a rename.
  // Those two ARE the same name as far as every calibre comparison is concerned,
  // and flagging them would train the admin to ignore the note.
  const renamed =
    normalizeCalibreKey(check.matchedName) !== normalizeCalibreKey(proposed);
  if (!renamed) return null;

  return {
    kind: 'renamed',
    message: `Links to “${check.matchedName}”.`,
    emphasis: check.matchedName,
  };
}

/**
 * Run the catalogue check for one AI-proposed calibre name.
 *
 * Pure over a corpus the caller loaded, for the same reason
 * {@link resolveCalibreForRef} is: the reference editor shortlists through
 * Algolia while the bulk grid already holds the whole live collection, and
 * neither should have its loading strategy dictated here.
 *
 * `unavailable` is the caller's to report — only it knows whether its load
 * succeeded — so an empty corpus passed with `unavailable: false` means a real
 * "nothing matches", which is exactly what a brand-new calibre looks like.
 */
export function checkAiCalibreMatch(
  candidates: readonly CustomCalibreEntry[],
  proposedName: string,
  brand: { id?: string; name?: string },
  unavailable = false,
): AiCalibreMatchCheck {
  const proposed = proposedName.trim();
  if (!proposed || unavailable) {
    return { proposed, matchedName: null, unavailable };
  }
  const resolved = resolveCalibreForRef(
    candidates,
    proposed,
    brand,
    AI_CALIBRE_MATCH_OPTIONS,
  );
  return {
    proposed,
    matchedName: resolved?.entry.name?.trim() || null,
    unavailable: false,
  };
}

/**
 * True when `tierId` names no tier the calibre defines, so the pointer is
 * dangling and must be cleared.
 *
 * Deliberately checks the FULL `tiers[]` rather than the brand-scoped subset.
 * Scoping decides which tiers may be OFFERED to a brand (and which the AI is
 * shown); a pointer that is already stored stays valid as long as the tier
 * exists, which is the same rule the calibre picker applies in
 * `handlePickCalibre`. Narrowing here would silently drop a deliberately-set
 * foreign-brand tier the moment any AI suggestion was accepted.
 *
 * An empty `tierId` is never stale — there is nothing to clear.
 */
export function isTierStaleOnCalibre(
  tierId: string,
  tiers: readonly CalibreTier[] | undefined,
): boolean {
  const id = tierId.trim();
  if (!id) return false;
  return !(tiers ?? []).some((t) => t.id === id);
}
